import { Divider, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler } from 'react';
import { useFormItem } from '../../../common/hooks/use-form-item';
import { InputType } from '../../../common/hooks/use-form-item/types';
import { maxLength } from '../../../common/hooks/use-form-item/validators/operational';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { DangerButton, PrimaryButton, TertiaryButton } from '../../../modal-flows/components/Buttons';
import { required } from '../../../common/hooks/use-form-item/validators/existential';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';

type Props = {
  jiraBaseUrl: string;
  jiraProjectKey: string;
  jiraIssueType: string;
  isJiraExportConfigured: boolean;
  handleClickCancel: () => void;
  handleClickSave: (projectKey: string, issueType: string) => void;
  handleClickUnlink: () => void;
  handleClickTestConnection: (projectKey: string, issueType: string) => void;
};

enum JiraIssues {
  TASK = 'Task',
  STORY = 'Story',
  EPIC = 'Epic',
  BUG = 'Bug',
}

export const JIRA_ISSUE_TYPES = [
  { value: JiraIssues.TASK, label: JiraIssues.TASK },
  { value: JiraIssues.STORY, label: JiraIssues.STORY },
  { value: JiraIssues.EPIC, label: JiraIssues.EPIC },
  { value: JiraIssues.BUG, label: JiraIssues.BUG },
];

export const JiraProjectForm = ({
  jiraBaseUrl,
  jiraProjectKey,
  jiraIssueType,
  isJiraExportConfigured,
  handleClickCancel,
  handleClickSave,
  handleClickUnlink,
  handleClickTestConnection,
}: Props) => {
  const [_, baseUrlComponent] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('jiraIssueExport.baseUrlInputLabel'),
    value: jiraBaseUrl,
    validator: null,
    inputOptions: {
      disabled: true,
    },
  });
  const [projectKey, projectKeyComponent, projectKeyValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('jiraIssueExport.projectKeyInputLabel'),
    value: jiraProjectKey || '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      autoFocus: true,
      showRequiredIndicator: true,
    },
    textOptions: { trimOnBlur: true },
  });

  const [issueType, issueTypeComponent] = useFormItem<string>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('jiraIssueExport.issueTypeInputLabel'),
    value: jiraIssueType || '',
    validator: required(),
    inputOptions: {
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: true,
      options: JIRA_ISSUE_TYPES,
    },
  });

  // Once they are configuring at least one field,
  // submit can only happen if both fields are set.
  // Don't allow submit if baseUrl doesn't exist
  const isFormValid = !!projectKey && !!issueType && projectKeyValid;
  const fieldsModified = jiraProjectKey !== projectKey || jiraIssueType !== issueType;
  const canSubmit = fieldsModified && isFormValid;
  const jiraFieldsConfigured = !!jiraProjectKey && !!jiraIssueType;

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    handleClickSave(projectKey, issueType);
  };

  const handleTestClick = () => {
    handleClickTestConnection(projectKey, issueType);
  };

  const message = isJiraExportConfigured ? 'jiraIssueExport.configuredIntegration.message' : 'jiraIssueExport.finishConfiguration.instruction';
  const secondaryMessage = !isJiraExportConfigured
    ? 'jiraIssueExport.finishConfiguration.secondaryInstruction'
    : jiraFieldsConfigured
    ? 'jiraIssueExport.configuredIntegration.secondaryMessage.jiraFieldsConfigured'
    : 'jiraIssueExport.configuredIntegration.secondaryMessage.jiraFieldsNotConfigured';

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Interpolation i18nKey={message} />
      <Interpolation i18nKey={secondaryMessage} variant="body2" sx={{ color: 'text.secondary' }} />
      <Stack spacing={2} component="form" onSubmit={onSubmit}>
        <Stack spacing={1.5} sx={{ maxWidth: '30rem' }}>
          {baseUrlComponent}
          {projectKeyComponent}
          {issueTypeComponent}
        </Stack>
        <Divider textAlign="center" />
        <Stack direction={'row'} gap={2} sx={{ justifyContent: 'space-between' }}>
          {isJiraExportConfigured ? (
            <DangerButton onClick={handleClickUnlink}>{t('jiraIssueExport.unlink')}</DangerButton>
          ) : (
            <TertiaryButton onClick={handleClickCancel}>{t('jiraIssueExport.cancel')}</TertiaryButton>
          )}
          <PrimaryButton disabled={!isFormValid} onClick={handleTestClick} sx={{ ml: 'auto' }}>
            {t('integrationList.jiraExport.testButton')}
          </PrimaryButton>
          <PrimaryButton disabled={!canSubmit} type="submit">
            {isJiraExportConfigured ? t('jiraIssueExport.save') : t('jiraIssueExport.finish')}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Stack>
  );
};
